// Human-readable `AssetLocator` description — extracted from
// `ResponseAssetGrid.tsx` (return-knowledge-images-across-surfaces PR 2) so
// PR 4's report image rendering (`ReportImage.tsx`) renders the exact same
// "página/bloque/timestamp" wording instead of duplicating it. Never exposes
// a raw locator field verbatim (e.g. a Notion `block_id` never reaches the
// DOM) — only a description built from format/position/timestamp.

import type { AssetLocator } from "./asset-locator";

const CONTAINER_FORMAT_LABELS: Record<string, string> = {
  pdf: "PDF",
  pptx: "PPTX",
  docx: "DOCX",
  xlsx: "XLSX",
};

const POSITION_KIND_LABELS: Record<string, string> = {
  page: "Página",
  document: "Documento",
  slide: "Diapositiva",
  sheet: "Hoja",
};

function formatFrameTimestamp(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export function describeAssetLocator(locator: AssetLocator): string {
  switch (locator.kind) {
    case "whole_image":
      return locator.origin === "chat_attachment" ? "Adjunto del chat" : "Imagen de la fuente";
    case "notion_block":
      return `Bloque de Notion · #${locator.position}`;
    case "document_page":
      return `${CONTAINER_FORMAT_LABELS[locator.container_format] ?? locator.container_format} · página ${locator.position}`;
    case "document_image": {
      const positionLabel = POSITION_KIND_LABELS[locator.position_kind] ?? locator.position_kind;
      return `${CONTAINER_FORMAT_LABELS[locator.container_format] ?? locator.container_format} · ${positionLabel} ${locator.position} · imagen ${locator.image_ordinal}`;
    }
    case "video_frame":
      return `Video · ${formatFrameTimestamp(locator.timestamp_ms)}`;
    default: {
      // Exhaustiveness guard: every locator kind the backend can emit is a
      // case above. A future new kind must be handled here explicitly
      // rather than silently rendering nothing.
      const exhaustive: never = locator;
      return exhaustive;
    }
  }
}
